import React, { Fragment, ReactNode, useState } from 'react';
import type { SxProps, Theme } from '@mui/material';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useThemeContext } from '../../contexts/ThemeContext.js';

export type TableColumn<T> = {
  label: string;
  render: (row: T) => ReactNode;
  padding?: 'normal' | 'checkbox' | 'none';
  sx?: SxProps<Theme>;
};

type TableCardProps<T> = {
  title?: string;
  rows: T[];
  columns: Array<TableColumn<T>>;
  getRowKey: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  getRowSx?: (row: T) => SxProps<Theme> | undefined;
  actionButtons?: ReactNode;
  renderExpandedRow?: (row: T) => ReactNode;
  hideHeader?: boolean;
};

export default function TableCard<T>({
  title,
  rows,
  columns,
  getRowKey,
  onRowClick,
  getRowSx,
  actionButtons,
  renderExpandedRow,
  hideHeader = false,
}: TableCardProps<T>): JSX.Element {
  const { theme } = useThemeContext();
  const isClickable = Boolean(onRowClick);
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());

  const toggleRowExpanded = (rowKey: string | number, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.layout.defaults.backgroundColor,
        borderRadius: '16px',
        mt: 0,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 2 }}>
        {title && (
          <Typography
            variant="h6"
            sx={{
              color: 'text.primary',
              fontWeight: 700,
              mb: 1,
            }}
          >
            {title}
          </Typography>
        )}
        {actionButtons && <Box sx={{ display: 'flex', gap: 1 }}>{actionButtons}</Box>}
      </Box>
      <TableContainer component={Paper}>
        <Table>
          {!hideHeader && (
            <TableHead>
              <TableRow>
                {renderExpandedRow && (
                  <TableCell padding="checkbox" sx={{ pr: 0, width: 36 }} />
                )}
                {columns.map((column) => (
                  <TableCell
                    key={column.label}
                    padding={column.padding}
                    sx={column.sx}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {rows.map((row, index) => {
              const rowKey = String(getRowKey(row, index));
              const expanded = expandedRows.has(rowKey);
              const handleRowClick = () => {
                if (onRowClick) {
                  onRowClick(row);
                }
              };

              return (
                <Fragment key={rowKey}>
                  <TableRow
                    onClick={onRowClick ? handleRowClick : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleRowClick();
                            }
                          }
                        : undefined
                    }
                    role={onRowClick ? 'button' : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    sx={{
                      ...(isClickable
                        ? {
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#f5f5f5' },
                            '&:focus-visible': {
                              outline: `2px solid ${theme.palette.primary.main}`,
                              outlineOffset: '-2px',
                            },
                          }
                        : {}),
                      ...(getRowSx?.(row) || {}),
                    }}
                  >
                    {renderExpandedRow && (
                      <TableCell padding="checkbox" sx={{ pr: 0, width: 36 }}>
                        <IconButton
                          size="small"
                          onClick={(event) => toggleRowExpanded(rowKey, event)}
                        >
                          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell
                        key={column.label}
                        padding={column.padding}
                        sx={column.sx}
                      >
                        {column.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                  {renderExpandedRow ? (
                    <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                      <TableCell colSpan={columns.length + 1} sx={{ p: 0 }}>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                          <Box sx={{ backgroundColor: 'background.default', py: 2, px: 3 }}>
                            {renderExpandedRow(row)}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
